// Exportação (MET-28) — `/v1/metricas/export`.
//
// O caso SÍNCRONO fica FORA do RTK Query: a resposta é binária, e o RTK Query
// guardaria o blob na store até o `keepUnusedDataFor` expirar — um xlsx de
// alguns MB preso em memória sem ninguém para lê-lo. É o mesmo motivo pelo qual
// `downloadStoreCatalogXlsx` também vive fora.
//
// O corte é do SERVIDOR, não da UI: ≤ 5000 linhas devolve `200` binário; acima,
// `202 { exportId, async: true }`. O front descobre qual dos dois pelo
// `content-type` da resposta — não tenta adivinhar antes de pedir.
import { api } from '@/lib/api';
import { getToken } from '@/lib/auth/token-storage';
import type {
  MetricsExportAcceptedDto,
  MetricsExportFormat,
  MetricsExportJobDto,
} from '../model';
import type { MetricsFilterArgs } from '../slice';

export interface MetricsExportBody extends MetricsFilterArgs {
  format: MetricsExportFormat;
  /** Seções a exportar. Ausente = todas as visíveis para o papel. */
  sections?: string[];
}

/** O que o POST devolveu: arquivo já baixado, ou job assíncrono a acompanhar. */
export type MetricsExportOutcome =
  | { kind: 'downloaded'; filename: string }
  | { kind: 'queued'; job: MetricsExportAcceptedDto };

/**
 * Nome do arquivo com as datas RESOLVIDAS, nunca o preset.
 *
 * `indicadores-7d.xlsx` é ambíguo no disco do gestor: daqui a um mês ninguém
 * sabe quais sete dias eram. O envelope já resolve o período — usamos ele.
 */
export function exportFilename(
  from: string | undefined,
  to: string | undefined,
  format: MetricsExportFormat,
): string {
  const ext = format.toLowerCase();
  const day = (iso?: string) => (iso ? iso.slice(0, 10) : null);
  const de = day(from);
  const ate = day(to);
  if (de && ate) return `indicadores-${de}_a_${ate}.${ext}`;
  // Sem intervalo resolvido, a data de HOJE ainda é melhor que um preset:
  // diz quando o arquivo foi gerado, que é informação verdadeira.
  return `indicadores-${new Date().toISOString().slice(0, 10)}.${ext}`;
}

function filenameFromHeaders(headers: Headers, fallback: string): string {
  // O back pode mandar o nome definitivo; se mandar, ele ganha.
  const disposition = headers.get('content-disposition');
  const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match?.[1] ? decodeURIComponent(match[1]) : fallback;
}

/**
 * Dispara a exportação. Baixa o arquivo quando vem binário; devolve o job quando
 * o servidor decidiu processar em background.
 */
export async function requestMetricsExport(
  body: MetricsExportBody,
): Promise<MetricsExportOutcome> {
  const token = getToken();
  const res = await fetch('/api/v1/metricas/export', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new Error(`Falha ao exportar (${res.status}).`);
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return { kind: 'queued', job: (await res.json()) as MetricsExportAcceptedDto };
  }

  const fallback = exportFilename(body.from, body.to, body.format);
  const filename = filenameFromHeaders(res.headers, fallback);
  await saveBlob(await res.blob(), filename);
  return { kind: 'downloaded', filename };
}

/**
 * Blob → download. O `revokeObjectURL` é obrigatório: sem ele cada exportação
 * deixa o arquivo inteiro retido em memória até a aba fechar, e o vazamento não
 * aparece em teste nenhum — só num gestor que exportou vinte vezes no dia.
 */
export async function saveBlob(blob: Blob, filename: string): Promise<void> {
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Baixa o arquivo de um job assíncrono já concluído. */
export async function downloadFinishedExport(job: MetricsExportJobDto): Promise<void> {
  const token = getToken();
  const res = await fetch(`/api/v1/metricas/export/${job.exportId}/download`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Falha ao baixar o arquivo (${res.status}).`);
  // O job passou a trazer as datas RESOLVIDAS (12/08), então o fallback deixa de
  // ser "indicadores-<hoje>" e nomeia o recorte de verdade. O nome do servidor
  // continua ganhando quando vem — e agora ele também traz as datas, em vez do
  // cuid opaco que eu tinha reportado.
  const fallback = exportFilename(job.from, job.to, job.format);
  await saveBlob(await res.blob(), filenameFromHeaders(res.headers, fallback));
}

/** O acompanhamento do job É JSON, então este fica no RTK Query. */
export const metricsExportApi = api.injectEndpoints({
  endpoints: (build) => ({
    metricsExportJob: build.query<MetricsExportJobDto, { exportId: string }>({
      query: ({ exportId }) => `/metricas/export/${exportId}`,
      providesTags: ['MetricsExportJob'],
    }),
  }),
  overrideExisting: false,
});

export const { useMetricsExportJobQuery } = metricsExportApi;
